/**
 * StatisticalCalculationService - Zentrale statistische Berechnungen für historische Finanzdaten
 *
 * Dieser Service konsolidiert alle statistischen Berechnungsmethoden und eliminiert Code-Duplikate
 * aus den verschiedenen Konstanten-Dateien. Er bildet die mathematische Grundlage für alle
 * Monte-Carlo-Simulationen und historischen Datenauswertungen.
 *
 * Hauptfunktionen:
 * - Berechnung umfassender Deskriptivstatistiken (μ, σ, min, max, Perzentile)
 * - Verarbeitung historischer deutscher Finanzmarktdaten (1992-2023)
 * - Normalisierung verschiedener Datenstrukturen für einheitliche Auswertung
 * - Typsichere statistische Algorithmen mit Fehlerbehandlung
 * - Support für deutsche Inflation, Tagesgeld, Staatsanleihen und internationale Indizes
 *
 * Mathematische Basis für zuverlässige Monte-Carlo-Simulationen mit bis zu 10.000 Iterationen.
 *
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 1.0.0
 */

/**
 * Vollständige Deskriptivstatistiken für historische Finanzmarktdaten
 *
 * Diese umfassende Datenstruktur enthält alle relevanten statistischen Kennzahlen
 * für die Monte-Carlo-Simulation und historische Datenanalyse.
 */
export interface BaseStatistics {
  /** Arithmetisches Mittel der Zeitreihe (in Prozent) */
  mean: number;

  /** Standardabweichung als Volatilitätsmaß (in Prozent) */
  stdDev: number;

  /** Niedrigster Wert in der Zeitreihe (für Worst-Case-Szenarien) */
  min: number;

  /** Höchster Wert in der Zeitreihe (für Best-Case-Szenarien) */
  max: number;

  /** Jahr mit dem niedrigsten Wert (für historische Kontextualisierung) */
  minYear: number;

  /** Jahr mit dem höchsten Wert (für historische Kontextualisierung) */
  maxYear: number;

  /** Anzahl der Datenpunkte (für Verlässlichkeitsschätzung) */
  count: number;

  /** Zeitraum der Daten im Format "YYYY-YYYY" (z.B. "1992-2023") */
  years: string;

  /** Alle Einzelwerte für Monte-Carlo-Sampling */
  rates: number[];

  /** Strukturierte Jahres-Wert-Paare für detaillierte Analyse */
  dataTable: Array<{ year: number; rate: number }>;
}

/**
 * Einzelner Datenpunkt in einer historischen Zeitreihe
 *
 * Repräsentiert einen spezifischen Wert für ein bestimmtes Jahr
 * in den historischen Finanzmarktdaten.
 */
export interface DataPoint {
  /** Jahr des Datenpunkts (z.B. 2008 für Finanzkrise) */
  year: number;

  /** Wert in Prozent (z.B. Inflationsrate, Rendite, Zinssatz) */
  rate: number;
}

/**
 * StatisticalCalculationService - Hauptklasse für statistische Finanzmarktdaten-Analyse
 *
 * Diese Klasse bietet typsichere, mathematisch korrekte Methoden für die Auswertung
 * historischer Finanzmarktdaten und bildet das Fundament aller probabilistischen
 * Simulationen in der Portfolio-3Topf-Anwendung.
 */
export class StatisticalCalculationService {
  /**
   * Berechnet umfassende Deskriptivstatistiken für beliebige Zeitreihen-Finanzdaten
   *
   * Diese zentrale Methode verarbeitet Rohdaten verschiedener Strukturen und liefert
   * einheitliche, vollständige Statistiken für Monte-Carlo-Simulationen und Analysen.
   *
   * @param data - Rohdaten-Objekt mit Jahr-zu-Wert-Mappings
   * @param countryKey - Länder-Schlüssel für die Datenextraktion (z.B. 'germany', 'world')
   * @param dataKey - Sekundärer Schlüssel für verschachtelte Datenstrukturen
   * @returns Vollständige BaseStatistics mit allen relevanten Kennzahlen
   *
   * @example
   * ```typescript
   * const inflationData = { "2020": 0.5, "2021": 3.1, "2022": 6.9, "2023": 3.7 };
   * const stats = StatisticalCalculationService.calculateStatistics(
   *   inflationData,
   *   'germany',
   *   'germany'
   * );
   * console.log(`Ø Inflation: ${stats.mean.toFixed(2)}% (σ=${stats.stdDev.toFixed(2)}%)`);
   * console.log(`Zeitraum: ${stats.years}, n=${stats.count} Jahre`);
   * ```
   *
   * @throws Error wenn keine gültigen Datenpunkte gefunden werden
   */
  static calculateStatistics(
    data: Record<string, any>,
    countryKey: string,
    dataKey: string = 'germany'
  ): BaseStatistics {
    // Extract data points
    const dataPoints = Object.entries(data)
      .map(([year, dataValue]) => ({
        year: parseInt(year),
        rate: this.extractValue(dataValue, countryKey, dataKey),
      }))
      .filter(item => item.rate !== null && item.rate !== undefined)
      .sort((a, b) => a.year - b.year) as DataPoint[];

    if (dataPoints.length === 0) {
      throw new Error('No valid data points found');
    }

    const rates = dataPoints.map(item => item.rate);

    // Calculate statistics
    const mean = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
    // Stichproben-Varianz (Bessel-Korrektur, ÷(n-1)): historische Jahresdaten
    // sind eine Stichprobe aus der zugrundeliegenden Renditeverteilung, nicht
    // die Grundgesamtheit selbst - konsistent mit calculateStandardDeviation
    // (siehe unten) und statisticalHelpers.ts. Bei n=1 ist keine Varianz
    // schätzbar (Division durch 0 vermeiden).
    const variance =
      rates.length > 1
        ? rates.reduce((sum, rate) => sum + Math.pow(rate - mean, 2), 0) / (rates.length - 1)
        : 0;
    const stdDev = Math.sqrt(variance);

    const min = Math.min(...rates);
    const max = Math.max(...rates);
    const minYear = dataPoints.find(item => item.rate === min)?.year || 0;
    const maxYear = dataPoints.find(item => item.rate === max)?.year || 0;

    const yearRange = `${Math.min(...dataPoints.map(item => item.year))}-${Math.max(...dataPoints.map(item => item.year))}`;

    return {
      // mean/stdDev werden ungerundet zurückgegeben: sie speisen als
      // Verteilungsparameter direkt die "statistisch-*"-Ziehungen in
      // RateCalculationService, eine Rundung auf 2 Nachkommastellen im
      // zurückgegebenen Wert (statt nur bei der Anzeige) würde diese
      // Parameter unnötig verfälschen.
      mean,
      stdDev,
      min: Number(min.toFixed(1)),
      max: Number(max.toFixed(1)),
      minYear,
      maxYear,
      count: rates.length,
      years: yearRange,
      rates,
      dataTable: dataPoints,
    };
  }

  /**
   * Berechnet Statistiken für deutsche Inflationsdaten (Verbraucherpreisindex)
   *
   * Spezialisierte Methode für die Auswertung deutscher Inflationsdaten basierend auf
   * dem Verbraucherpreisindex (VPI) des Statistischen Bundesamts.
   *
   * @param inflationData - Deutsche Inflationsdaten (Jahr -> Inflationsrate in %)
   * @returns BaseStatistics für deutsche Inflationsentwicklung
   *
   * @example
   * ```typescript
   * const germanInflation = { "2020": 0.5, "2021": 3.1, "2022": 6.9 };
   * const stats = StatisticalCalculationService.calculateInflationStatistics(germanInflation);
   * console.log(`Deutsche Inflation: μ=${stats.mean}%, σ=${stats.stdDev}%`);
   * console.log(`Range: ${stats.min}% (${stats.minYear}) bis ${stats.max}% (${stats.maxYear})`);
   * ```
   */
  static calculateInflationStatistics(inflationData: Record<string, any>): BaseStatistics {
    return this.calculateStatistics(inflationData, 'germany', 'germany');
  }

  /**
   * Berechnet Statistiken für deutsche Tagesgeld-Zinssätze
   *
   * Analysiert historische deutsche Tagesgeld-Zinssätze als Basis für
   * Verbrauchstopf-Renditeberechnungen in den Simulationen.
   *
   * @param tagesgeldData - Deutsche Tagesgeld-Daten (Jahr -> Zinssatz in %)
   * @returns BaseStatistics für deutsche Tagesgeld-Entwicklung
   *
   * @example
   * ```typescript
   * const tagesgeldData = { "2020": 0.01, "2021": 0.01, "2022": 0.1, "2023": 1.8 };
   * const stats = StatisticalCalculationService.calculateTagesgeldStatistics(tagesgeldData);
   * console.log(`Tagesgeld-Zinsen: Ø ${stats.mean}% p.a.`);
   * ```
   */
  static calculateTagesgeldStatistics(tagesgeldData: Record<string, any>): BaseStatistics {
    return this.calculateStatistics(tagesgeldData, 'germany', 'germany');
  }

  /**
   * Berechnet Statistiken für deutsche Zinstopf-Renditen (Staatsanleihen/Festgeld)
   *
   * Analysiert historische Renditen sicherer deutscher Anleihen und Festgeld-Anlagen
   * als Basis für mittelfristige Zinstopf-Strategien.
   *
   * @param zinstopfData - Deutsche Zinstopf-Daten (Jahr -> Rendite in %)
   * @returns BaseStatistics für deutsche Zinstopf-Entwicklung
   *
   * @example
   * ```typescript
   * const zinstopfData = { "2020": 0.5, "2021": 0.3, "2022": 1.2, "2023": 2.8 };
   * const stats = StatisticalCalculationService.calculateZinstopfStatistics(zinstopfData);
   * console.log(`Zinstopf-Renditen: ${stats.mean}% ± ${stats.stdDev}%`);
   * ```
   */
  static calculateZinstopfStatistics(zinstopfData: Record<string, any>): BaseStatistics {
    return this.calculateStatistics(zinstopfData, 'germany', 'germany');
  }

  /**
   * Berechnet Statistiken für internationale Aktienindex-Renditen
   *
   * Analysiert historische Renditen globaler Aktienindizes (MSCI World, MSCI ACWI)
   * als Basis für Investitionstopf-Strategien in der Portfolio-Optimierung.
   *
   * @param indexData - Index-Rendite-Daten (Jahr -> Rendite in %)
   * @param index - Index-Typ ('world' für MSCI World, 'acwi' für MSCI ACWI)
   * @returns BaseStatistics für die gewählte Index-Entwicklung
   *
   * @example
   * ```typescript
   * const msciWorldData = { "2020": 15.9, "2021": 21.8, "2022": -18.1, "2023": 18.8 };
   * const stats = StatisticalCalculationService.calculateIndexStatistics(msciWorldData, 'world');
   * console.log(`MSCI World: μ=${stats.mean}% p.a., Volatilität=${stats.stdDev}%`);
   * console.log(`Schlechtestes Jahr: ${stats.min}% (${stats.minYear})`);
   * ```
   */
  static calculateIndexStatistics(
    indexData: Record<string, any>,
    index: 'world' | 'acwi' = 'world'
  ): BaseStatistics {
    return this.calculateStatistics(indexData, index, index);
  }

  /**
   * Extrahiert numerische Werte aus komplexen, verschachtelten Datenstrukturen
   *
   * Diese Hilfsmethode normalisiert verschiedene Datenformate aus externen Quellen
   * (APIs, CSV-Importe, JSON-Daten) zu einheitlichen numerischen Werten.
   *
   * @param dataValue - Rohdatum (kann number, object, oder andere Struktur sein)
   * @param countryKey - Primärer Schlüssel für Länder-/Index-spezifische Daten
   * @param dataKey - Sekundärer Schlüssel für verschachtelte Strukturen
   * @returns Extrahierter numerischer Wert oder null bei ungültigen Daten
   *
   * @example
   * ```typescript
   * // Verschiedene Datenstrukturen
   * const simpleValue = 2.5; // → 2.5
   * const nestedData = { germany: 1.8, usa: 2.1 }; // → 1.8 für 'germany'
   * const complexData = { world: { annual: 7.2 } }; // → 7.2 für 'world', 'annual'
   *
   * const extracted = StatisticalCalculationService.extractValue(nestedData, 'germany', 'germany');
   * console.log(`Deutscher Wert: ${extracted}%`); // "1.8%"
   * ```
   *
   * @private Diese Methode ist nur für interne Datenverarbeitung gedacht
   */
  private static extractValue(dataValue: any, countryKey: string, dataKey: string): number | null {
    if (typeof dataValue === 'number') {
      return dataValue;
    }

    if (typeof dataValue === 'object' && dataValue !== null) {
      // For structures like { germany: 1.5 } or { world: 2.3, acwi: 2.1 }
      if (dataValue[countryKey] !== undefined) {
        return dataValue[countryKey];
      }
      if (dataValue[dataKey] !== undefined) {
        return dataValue[dataKey];
      }
    }

    return null;
  }

  /**
   * Berechnet das arithmetische Mittel einer Zahlenreihe
   *
   * Diese grundlegende statistische Funktion berechnet den Durchschnittswert
   * einer numerischen Zeitreihe für weitere statistische Analysen.
   *
   * @param values - Array numerischer Werte (z.B. jährliche Renditen)
   * @returns Arithmetisches Mittel oder 0 bei leerem Array
   *
   * @example
   * ```typescript
   * const returns = [8.5, -2.1, 15.3, 6.7, -8.9];
   * const mean = StatisticalCalculationService.calculateMean(returns);
   * console.log(`Durchschnittsrendite: ${mean.toFixed(2)}%`); // "3.9%"
   * ```
   */
  static calculateMean(values: number[]): number {
    if (values.length === 0) {return 0;}
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  /**
   * Berechnet die Standardabweichung einer Zahlenreihe (Populationsstandardabweichung)
   *
   * Die Standardabweichung ist das wichtigste Risikomaß in der Finanzmarkttheorie
   * und misst die Volatilität von Renditen um den Mittelwert.
   *
   * @param values - Array numerischer Werte für Volatilitätsberechnung
   * @returns Standardabweichung oder 0 bei leerem Array
   *
   * @example
   * ```typescript
   * const returns = [8.5, -2.1, 15.3, 6.7, -8.9];
   * const volatility = StatisticalCalculationService.calculateStandardDeviation(returns);
   * console.log(`Volatilität: ${volatility.toFixed(2)}%`); // "9.13%"
   *
   * // Typische Anwendung für Risikobewertung
   * const riskLevel = volatility > 15 ? 'Hoch' : volatility > 10 ? 'Mittel' : 'Niedrig';
   * ```
   *
   * @note Verwendet Stichproben-Formel (Bessel-Korrektur): σ = √(Σ(xi - μ)² / (n-1))
   */
  static calculateStandardDeviation(values: number[]): number {
    if (values.length < 2) {return 0;}
    const mean = this.calculateMean(values);
    const variance =
      values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / (values.length - 1);
    return Math.sqrt(variance);
  }

  /**
   * Ermittelt Minimum und Maximum einer Zahlenreihe für Extremwert-Analyse
   *
   * Diese Funktion identifiziert die Extremwerte in historischen Daten,
   * die für Worst-Case- und Best-Case-Szenario-Analysen verwendet werden.
   *
   * @param values - Array numerischer Werte zur Extremwert-Ermittlung
   * @returns Objekt mit min und max Werten, oder {0, 0} bei leerem Array
   *
   * @example
   * ```typescript
   * const returns = [8.5, -18.1, 28.7, 6.7, -8.9]; // Crashjahr und Boomjahr enthalten
   * const { min, max } = StatisticalCalculationService.getMinMax(returns);
   * console.log(`Worst Case: ${min}%, Best Case: ${max}%`); // "-18.1%, 28.7%"
   *
   * // Risk Assessment basierend auf Extremwerten
   * const range = max - min;
   * console.log(`Schwankungsbreite: ${range.toFixed(1)}%`); // "46.8%"
   * ```
   */
  static getMinMax(values: number[]): { min: number; max: number } {
    if (values.length === 0) {return { min: 0, max: 0 };}
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }
}
